import React from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Typography,
  Divider,
  Select,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RatecardClientInvoiceResponse } from "../../../../app/models/responseModels/ClientInvoiceResponses";

interface InvoiceActionMenuProps {
  invoice: RatecardClientInvoiceResponse | undefined;
  handleApprove: (submitType?: string) => void;
  handleApproveRadioClick: () => void;
  handleEdit: CallableFunction;
  handleFlag?: CallableFunction;
  resetApproveSelection?: boolean;
}

const InvoiceActionMenu = ({
  invoice,
  handleApprove,
  handleApproveRadioClick,
  handleEdit,
  handleFlag,
  resetApproveSelection,
}: InvoiceActionMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedValue, setSelectedValue] = React.useState<string>("");
  const [approveType, setApproveType] = React.useState<string>("");
  const open = Boolean(anchorEl);

  React.useEffect(() => {
    setSelectedValue("");
    setApproveType("");
  }, [resetApproveSelection]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedValue("");
    setApproveType("");
  };

  const handleSubmitReview = () => {
    if (selectedValue === "edit") {
      handleEdit();
    }
    if (selectedValue === "approve") {
      handleApprove(approveType || undefined);
    }
    if (selectedValue === "comment" && handleFlag) {
      handleFlag();
    }
    handleClose();
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedValue(value);
    setApproveType("");
    if (value === "approve") {
      handleApproveRadioClick();
    }
  };

  const showApprovalDropdown = selectedValue === "approve" && 
    invoice?.currentStatus.externalStatus === "Pending Fin approval";

  const isSubmitDisabled = !selectedValue || (showApprovalDropdown && !approveType);

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Button variant="outlined" size="small" onClick={handleClick}>
        Actions
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        sx={{ mt: -2 }}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 280,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" pl={1}>
            Provide Review
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <RadioGroup value={selectedValue} onChange={handleRadioChange}>
          <MenuItem key={"edit"} sx={{ py: 0 }}>
            <FormControlLabel
              value="edit"
              disabled={!invoice?.editable}
              control={<Radio size="medium" />}
              label={<Typography variant="body2">Edit</Typography>}
            />
          </MenuItem>
          <MenuItem key={"comment"} sx={{ py: 0 }}>
            <FormControlLabel
              disabled={!invoice?.flagable}
              value="comment"
              control={<Radio size="medium" />}
              label={<Typography variant="body2">Comment</Typography>}
            />
          </MenuItem>
          <MenuItem key={"approve"} sx={{ py: 0 }}>
            <FormControlLabel
              value="approve"
              disabled={!invoice?.approvable}
              control={<Radio size="medium" />}
              label={<Typography variant="body2">Approve</Typography>}
            />
          </MenuItem>
        </RadioGroup>
        {showApprovalDropdown && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 2, pb: 1 }}>
              <FormControl fullWidth size="small">
                <Select
                  native
                  value={approveType}
                  onChange={(e) => setApproveType(e.target.value)}
                  displayEmpty
                >
                  <option value="" disabled hidden>Select Approval Type</option>
                  <option value="Internal_Approval">Internal Approval</option>
                  <option value="Submit_to_Client">Submit to Client</option>
                </Select>
              </FormControl>
            </Box>
          </>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 1 }}>
          <Button
            size="small"
            disabled={isSubmitDisabled}
            variant="contained"
            onClick={handleSubmitReview}
          >
            Submit
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default InvoiceActionMenu;
